export const areDagRecordsEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length
    && keys.every((key) => Object.prototype.hasOwnProperty.call(right, key)
      && left[key] === right[key]);
};

const reuseRecord = (current, next) => areDagRecordsEqual(current, next) ? current : next;

// Configuration invalidations share one request, with one trailing read if a
// change arrived after that request started. Each canvas owns its own queue.
export const createDagRefreshQueue = (load, { onError, delay = 50, timeout = 60000 }) => {
  let active = null;
  let timer = null;
  let pending = false;
  const complete = (request) => {
    if (active !== request) return;
    clearTimeout(request.timeout);
    active = null;
    if (pending) timer = setTimeout(start, delay);
  };
  const start = () => {
    timer = null;
    if (active || !pending) return;
    pending = false;
    const request = {};
    active = request;
    const fail = (error) => {
      if (active !== request) return;
      onError(error);
      complete(request);
    };
    request.timeout = setTimeout(() => fail(new Error('DAG configuration request timed out')), timeout);
    try {
      const result = load({ complete: () => complete(request), isCurrent: () => active === request });
      if (result?.catch) result.catch(fail);
    } catch (error) {
      fail(error);
    }
  };
  return {
    request() {
      pending = true;
      if (!active && timer === null) start();
      return true;
    },
    cancel() {
      clearTimeout(timer);
      if (active) clearTimeout(active.timeout);
      timer = null;
      active = null;
      pending = false;
    },
  };
};

// Coalesce preview work while React Flow continues receiving every drag position.
export const createDagFrameScheduler = (
  callback,
  requestFrame = requestAnimationFrame,
  cancelFrame = cancelAnimationFrame
) => {
  let frameId = null;
  let latestValue;
  const cancel = () => {
    if (frameId !== null) cancelFrame(frameId);
    frameId = null;
    latestValue = undefined;
  };
  return {
    schedule(value) {
      latestValue = value;
      if (frameId !== null) return;
      frameId = requestFrame(() => {
        const valueToProcess = latestValue;
        frameId = null;
        latestValue = undefined;
        callback(valueToProcess);
      });
    },
    cancel,
  };
};

// React Flow accepts an array of node IDs and updates its store once for the
// batch. Each canvas owns its own queue so projects cannot share pending work.
export const createDagNodeInternalsBatcher = (
  requestFrame = requestAnimationFrame,
  cancelFrame = cancelAnimationFrame
) => {
  const pending = new Set();
  let frameId = null;
  let updateInternals = null;
  return {
    schedule(id, update) {
      pending.add(id);
      updateInternals = update;
      if (frameId !== null) return;
      frameId = requestFrame(() => {
        frameId = null;
        const ids = [...pending];
        pending.clear();
        const callback = updateInternals;
        updateInternals = null;
        if (ids.length) callback(ids);
      });
    },
    cancel() {
      if (frameId !== null) cancelFrame(frameId);
      frameId = null;
      pending.clear();
      updateInternals = null;
    },
  };
};

// Keep React Flow's selection, measurements and unchanged data references.
// Inputs must be updated immutably, as with React state elsewhere in the app.
export const reuseDagElements = (currentElements, nextElements) => {
  const currentById = new Map(currentElements.map((element) => [element.id, element]));
  const result = nextElements.map((next) => {
    const current = currentById.get(next.id);
    if (!current) return next;
    const data = { ...next.data };
    if (data.node && current.data?.node) {
      const anchors = data.node.handleAnchors;
      const previousAnchors = current.data.node.handleAnchors;
      const nextNode = { ...data.node };
      ['actionMeta', 'templateSetupStatus'].forEach((key) => {
        if (key in nextNode) nextNode[key] = reuseRecord(current.data.node[key], nextNode[key]);
      });
      if (anchors) {
        const handleAnchors = { ...anchors };
        Object.keys(anchors).forEach((key) => {
          handleAnchors[key] = reuseRecord(previousAnchors?.[key], anchors[key]);
        });
        nextNode.handleAnchors = reuseRecord(previousAnchors, handleAnchors);
      }
      data.node = reuseRecord(current.data.node, nextNode);
    }
    ['runtime', 'search', 'journalNode', 'flowNode'].forEach((key) => {
      if (key in data) data[key] = reuseRecord(current.data?.[key], data[key]);
    });
    const candidate = { ...current, ...next, data: reuseRecord(current.data, data) };
    ['position', 'style', 'markerEnd'].forEach((key) => {
      if (key in next) candidate[key] = reuseRecord(current[key], next[key]);
    });
    return reuseRecord(current, candidate);
  });
  return result.length === currentElements.length
    && result.every((element, index) => element === currentElements[index])
    ? currentElements : result;
};

export const shouldReduceDagEffects = (nodeCount, device = {}) => (
  nodeCount > 80
  || (device.hardwareConcurrency > 0 && device.hardwareConcurrency <= 4)
  || (device.deviceMemory > 0 && device.deviceMemory <= 4)
);

// Only endpoints of flow-to-flow links determine the side of those links.
// Unrelated node movement must not invalidate their geometry or the graph.
export const flowLinkPositionSignature = (nodes, endpointKeys, defaultPositions) => {
  if (!endpointKeys.size) return '[]';
  return JSON.stringify(nodes
    .filter((node) => endpointKeys.has(node.id)
      && Object.prototype.hasOwnProperty.call(defaultPositions, node.id)
      && Number.isFinite(Number(node.position?.x))
      && Number.isFinite(Number(node.position?.y)))
    .map((node) => [node.id, Number(node.position.x), Number(node.position.y)])
    .sort(([left], [right]) => String(left).localeCompare(String(right))));
};

// Local diagnostics contain numbers only; no source names, settings or tokens.
export const isDagLoadMeasurementEnabled = () => (
  process.env.NODE_ENV === 'development'
  || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('dagMetrics') === '1')
);

export const dagMeasurementNow = () => performance.now();

export const readDagRequestMeasurement = (path, startedAt) => {
  const finishedAt = dagMeasurementNow();
  const resource = performance.getEntriesByType('resource').slice(-250).reverse().find((entry) => (
    entry.name.endsWith(path) && entry.startTime >= startedAt
  ));
  return { request_ms: finishedAt - startedAt,
    resource_ms: resource?.duration || null,
    response_decode_ms: resource?.responseEnd ? Math.max(0, finishedAt - resource.responseEnd) : null,
    // Cross-origin privacy restrictions can hide detailed timing and byte counts.
    first_byte_ms: resource?.responseStart ? resource.responseStart - resource.startTime : null,
    transfer_bytes: resource?.transferSize || null };
};

export const readDagResourceMeasurements = (caseId, startedAt) => {
  const paths = {
    connectors: `/api/v1/projects/${encodeURIComponent(caseId)}/datasources/batch`,
    flows: `/api/v1/projects/${encodeURIComponent(caseId)}/workflow-flows`,
    actions: `/api/v1/projects/${encodeURIComponent(caseId)}/actions/export-settings`,
    layout: `/api/v1/projects/${encodeURIComponent(caseId)}/dag-layout`,
    action_catalog: '/api/v1/action-plugins',
  };
  const records = {};
  for (const entry of performance.getEntriesByType('resource').slice(-250)) {
    if (entry.startTime < startedAt) continue;
    const url = new URL(entry.name);
    const type = url.searchParams.get('view') === 'dag_details' ? 'action_details'
      : Object.keys(paths).find((key) => url.pathname === paths[key]);
    if (type) records[type] = { started_after_mount_ms: Math.round(entry.startTime - startedAt),
      request_ms: Math.round(entry.duration) };
  }
  return records;
};

export const reportDagLoadMeasurement = (measurement) => {
  if (!isDagLoadMeasurementEnabled()) return;
  const rounded = Object.fromEntries(Object.entries(measurement).map(([key, value]) => (
    [key, typeof value === 'number' ? Math.round(value * 10) / 10 : value]
  )));
  console.info('DAG connector load measurement', JSON.stringify(rounded));
};
